import { useState, useCallback } from 'react';
import type { MenuItem, ServiceType, DeckPriority } from '@/lib/api/services/orders.api';

// ─── Cart ────────────────────────────────────────────────────────────────────

export interface CartLine {
  menuItem: MenuItem;
  qty: number;
}

// ─── Step 2 — customer info ───────────────────────────────────────────────────

export interface CustomerInfo {
  serviceType: ServiceType;
  tableId: number | null;
  tableName: string;
  section: string;
  guestCount: number;
  serverId: number | null;
  serverName: string;
  guestName: string;
  phone: string;
  reservationTag: string;
  allergens: string[];
  kitchenNotes: string;
}

// ─── Step 3 — assignment ─────────────────────────────────────────────────────

export interface AssignmentInfo {
  leadChefId: number | null;
  leadChefName: string;
  priority: DeckPriority;
  targetMinutes: number;
  printChit: boolean;
  notifyAllergen: boolean;
}

// ─── Full state ───────────────────────────────────────────────────────────────

export interface OrderState {
  cart: CartLine[];
  customer: CustomerInfo;
  assignment: AssignmentInfo;
}

const DEFAULT_CUSTOMER: CustomerInfo = {
  serviceType: 'DINE_IN',
  tableId: null,
  tableName: '',
  section: '',
  guestCount: 2,
  serverId: null,
  serverName: '',
  guestName: '',
  phone: '',
  reservationTag: '',
  allergens: [],
  kitchenNotes: '',
};

const DEFAULT_ASSIGNMENT: AssignmentInfo = {
  leadChefId: null,
  leadChefName: '',
  priority: 'NORMAL',
  targetMinutes: 27,
  printChit: true,
  notifyAllergen: true,
};

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useOrderState() {
  const [cart, setCart] = useState<CartLine[]>([]);
  const [customer, setCustomer] = useState<CustomerInfo>(DEFAULT_CUSTOMER);
  const [assignment, setAssignment] = useState<AssignmentInfo>(DEFAULT_ASSIGNMENT);

  // Cart helpers
  const addItem = useCallback((item: MenuItem) => {
    setCart((prev) => {
      const existing = prev.find((l) => l.menuItem.id === item.id);
      if (existing) {
        return prev.map((l) =>
          l.menuItem.id === item.id ? { ...l, qty: l.qty + 1 } : l,
        );
      }
      return [...prev, { menuItem: item, qty: 1 }];
    });
  }, []);

  const setQty = useCallback((menuItemId: number, qty: number) => {
    setCart((prev) => {
      if (qty <= 0) return prev.filter((l) => l.menuItem.id !== menuItemId);
      return prev.map((l) => (l.menuItem.id === menuItemId ? { ...l, qty } : l));
    });
  }, []);

  const removeItem = useCallback((menuItemId: number) => {
    setCart((prev) => prev.filter((l) => l.menuItem.id !== menuItemId));
  }, []);

  const clearCart = useCallback(() => setCart([]), []);

  const isInCart = useCallback(
    (menuItemId: number) => cart.some((l) => l.menuItem.id === menuItemId),
    [cart],
  );

  // Totals
  const subtotal = cart.reduce(
    (sum, l) => sum + (l.menuItem.price ?? 0) * l.qty,
    0,
  );
  const tax = subtotal * 0.0825;
  const total = subtotal + tax;
  const itemCount = cart.reduce((n, l) => n + l.qty, 0);

  // Customer helpers
  const patchCustomer = useCallback(
    (patch: Partial<CustomerInfo>) => setCustomer((prev) => ({ ...prev, ...patch })),
    [],
  );

  const toggleAllergen = useCallback((allergen: string) => {
    setCustomer((prev) => {
      const already = prev.allergens.includes(allergen);
      return {
        ...prev,
        allergens: already
          ? prev.allergens.filter((a) => a !== allergen)
          : [...prev.allergens, allergen],
      };
    });
  }, []);

  // Assignment helpers
  const patchAssignment = useCallback(
    (patch: Partial<AssignmentInfo>) =>
      setAssignment((prev) => ({ ...prev, ...patch })),
    [],
  );

  return {
    cart,
    customer,
    assignment,
    // Cart
    addItem,
    setQty,
    removeItem,
    clearCart,
    isInCart,
    // Derived
    subtotal,
    tax,
    total,
    itemCount,
    // Customer
    patchCustomer,
    toggleAllergen,
    // Assignment
    patchAssignment,
  };
}
